import { useEffect, useMemo, useState } from "react";
import { OUTREACH_STAGES, OUTREACH_STAGE_ORDER } from "../../constants/dealStages";
import { followUpDays } from "../../services/outreachFlow";
import OutreachCard from "./OutreachCard";
import OutreachTable from "./OutreachTable";

// The Outreach section: LOIs we submitted to owners through brokers. Pipeline's
// dashboard - header, filters, table or cards grouped by stage - on the
// outreach flow (deals.deal_flow = 'outreach'). A deal leaves this view when
// it moves into Pipeline; `promotedCount` keeps those in the funnel.
export default function OutreachDashboard({ deals, promotedCount = 0, loading, onSelectDeal, photoUrls = {},
  onStageChange, onPromote, onUploadLOI, readOnly = false }) {
  const [filterMarket, setFilterMarket] = useState("All");
  const [filterStages, setFilterStages] = useState(new Set());
  const [hideDeclined, setHideDeclined] = useState(true);
  const [minSF, setMinSF] = useState("");
  const [maxSF, setMaxSF] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("loi_desc");
  const [layout, setLayout] = useState(() => localStorage.getItem("pp.outreachLayout") || "table");
  useEffect(() => { localStorage.setItem("pp.outreachLayout", layout); }, [layout]);

  const usedMarkets = useMemo(() => {
    const set = new Set();
    for (const d of deals) for (const m of (d.msas || [])) if (m) set.add(m);
    return [...set].sort();
  }, [deals]);

  const filtered = deals.filter((deal) => {
    if (filterMarket !== "All" && !(deal.msas || []).includes(filterMarket)
        && !(deal.market || "").split(",").map((s) => s.trim()).includes(filterMarket)) return false;
    if (filterStages.size > 0 && !filterStages.has(deal.stage)) return false;
    if (hideDeclined && deal.stage === "Declined") return false;
    if (minSF && deal.squareFeet && deal.squareFeet < Number(minSF)) return false;
    if (maxSF && deal.squareFeet && deal.squareFeet > Number(maxSF)) return false;
    if (minPrice && deal.offerPrice && deal.offerPrice < Number(minPrice)) return false;
    if (maxPrice && deal.offerPrice && deal.offerPrice > Number(maxPrice)) return false;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const hay = [deal.sourceNotes?.match(/^Name:\s*(.+)$/m)?.[1], ...(deal.addresses || []), deal.market,
        deal.brokerFirm, deal.brokerName].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sortWithin = (list) => [...list].sort((a, b) => {
    if (sortBy === "follow_asc") return (a.followUpDate || "9999").localeCompare(b.followUpDate || "9999");
    if (sortBy === "offer_desc") return (b.offerPrice || 0) - (a.offerPrice || 0);
    return (b.loiDate || b.createdAt || "").localeCompare(a.loiDate || a.createdAt || "");
  });
  const groups = [...OUTREACH_STAGES]
    .sort((a, b) => OUTREACH_STAGE_ORDER[a] - OUTREACH_STAGE_ORDER[b])
    .map((stage) => ({ stage, deals: sortWithin(filtered.filter((d) => d.stage === stage)) }))
    .filter((g) => g.deals.length > 0);

  // The funnel across every LOI ever sent, including the ones now in Pipeline.
  const sent = deals.length + promotedCount;
  const received = deals.filter((d) => d.confirmedReceiptAt).length + promotedCount;
  const countered = deals.filter((d) => d.counteredAt).length;
  const declined = deals.filter((d) => d.stage === "Declined").length;
  const overdue = deals.filter((d) => (followUpDays(d) ?? 1) < 0).length;
  const pct = (n) => (sent ? `${Math.round((n / sent) * 100)}%` : "—");

  const hasActiveFilters = filterMarket !== "All" || filterStages.size > 0 || !hideDeclined || minSF || maxSF || minPrice || maxPrice || searchTerm;
  const clearFilters = () => {
    setFilterMarket("All"); setFilterStages(new Set()); setHideDeclined(true);
    setMinSF(""); setMaxSF(""); setMinPrice(""); setMaxPrice(""); setSearchTerm("");
  };
  const filterInput = "bg-[#2c2c2c] border border-[#3a3a3a] text-[#b4b4b4] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#e93529] transition-colors";

  return (
    <main className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-4 flex items-baseline gap-3">
        <h2 className="text-lg font-semibold text-[#f4f4f4]">Outreach</h2>
        {!loading && (
          <span className="text-sm text-[#8a8a8a]">
            {filtered.length !== deals.length ? `${filtered.length} of ${deals.length} LOIs` : `${deals.length} LOIs`}
          </span>
        )}
      </div>

      {!loading && sent > 0 && (
        <div className="mb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ["LOIs sent", sent, "Every LOI submitted through a broker, including those now in Pipeline"],
            ["Confirmed receipt", `${received} · ${pct(received)}`, "The broker confirmed they received it"],
            ["Countered", `${countered + promotedCount} · ${pct(countered + promotedCount)}`, "The seller countered (moved-to-Pipeline deals count as countered)"],
            ["Moved to Pipeline", `${promotedCount} · ${pct(promotedCount)}`, "Became an active deal in Pipeline"],
            ["Follow-ups overdue", overdue, "Open LOIs past their follow-up date"],
          ].map(([label, value, title]) => (
            <div key={label} title={title} className="bg-[#242424] border border-[#3a3a3a] rounded-lg px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-[#8a8a8a] mb-1">{label}</div>
              <div className={`text-lg font-semibold ${label === "Follow-ups overdue" && overdue ? "text-[#f04f44]" : "text-[#f4f4f4]"}`}>{value}</div>
            </div>
          ))}
        </div>
      )}
      {!loading && declined > 0 && hideDeclined && (
        <p className="text-[11px] text-[#7a7a7a] -mt-3 mb-4">{declined} declined LOI{declined === 1 ? "" : "s"} hidden.</p>
      )}

      <div className="mb-4 inline-flex rounded-lg bg-[#2c2c2c] border border-[#3a3a3a] p-0.5">
        {[["table", "Table"], ["cards", "Cards"]].map(([k, label]) => (
          <button key={k} onClick={() => setLayout(k)}
            className={`px-3 py-1 rounded-md text-[12px] font-medium transition ${layout === k ? "bg-[#3a3a3a] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#f4f4f4]"}`}>
            {label}
          </button>
        ))}
      </div>

      {!loading && deals.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2 p-3 bg-[#242424] border border-[#3a3a3a] rounded-lg">
          <input type="text" placeholder="Search deal, address or broker..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className={filterInput + " w-60"} />
          <select value={filterMarket} onChange={(e) => setFilterMarket(e.target.value)} className={filterInput}>
            <option value="All">All Markets</option>
            {usedMarkets.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <div className="relative group">
            <button className={filterInput + " flex items-center gap-1 cursor-pointer"}>
              {filterStages.size === 0 ? "All Stages" : `${filterStages.size} Stage${filterStages.size > 1 ? "s" : ""}`}
              <span className="text-[#8a8a8a] text-xs">▾</span>
            </button>
            <div className="absolute top-full left-0 mt-1 z-20 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 min-w-[170px] hidden group-hover:block">
              {OUTREACH_STAGES.map((s) => (
                <label key={s} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[#3a3a3a] cursor-pointer" style={{ color: filterStages.has(s) ? "#e93529" : undefined }}>
                  <input type="checkbox" checked={filterStages.has(s)} className="accent-pp-red w-3.5 h-3.5"
                    onChange={() => setFilterStages((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; })} />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-[#8a8a8a] cursor-pointer select-none">
            <input type="checkbox" checked={hideDeclined} onChange={(e) => setHideDeclined(e.target.checked)} className="accent-pp-red" />
            Hide Declined
          </label>
          <div className="flex items-center gap-1 text-xs text-[#8a8a8a]">
            <span>Sort:</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={filterInput} title="Sort within each stage (cards)">
              <option value="loi_desc">LOI date — newest first</option>
              <option value="follow_asc">Follow-up — soonest first</option>
              <option value="offer_desc">Offer — largest first</option>
            </select>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#8a8a8a]">
            <span>SF:</span>
            <input type="number" placeholder="Min" value={minSF} onChange={(e) => setMinSF(e.target.value)} className={filterInput + " w-20"} />
            <span>–</span>
            <input type="number" placeholder="Max" value={maxSF} onChange={(e) => setMaxSF(e.target.value)} className={filterInput + " w-20"} />
          </div>
          <div className="flex items-center gap-1 text-xs text-[#8a8a8a]">
            <span>Offer $:</span>
            <input type="number" placeholder="Min" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className={filterInput + " w-24"} />
            <span>–</span>
            <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className={filterInput + " w-24"} />
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-[#f04f44] hover:text-[#e93529] transition-colors">Clear filters</button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-24 text-[#8a8a8a] text-sm">Loading outreach…</div>
      ) : deals.length === 0 ? (
        <div className="text-center py-24 text-[#8a8a8a]">
          <p className="text-5xl mb-4">✉️</p>
          <p className="text-lg">No broker LOIs yet.</p>
          {!readOnly && (
            <button onClick={onUploadLOI} className="mt-4 text-[#f04f44] hover:text-[#e93529] underline text-sm">Upload the first LOI</button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#8a8a8a]">
          <p className="text-sm">No LOIs match your filters.</p>
          <button onClick={clearFilters} className="mt-3 text-[#f04f44] hover:text-[#e93529] underline text-sm">Clear filters</button>
        </div>
      ) : layout === "table" ? (
        <OutreachTable deals={filtered} onSelectDeal={onSelectDeal} />
      ) : (
        <div className="flex flex-col gap-8">
          {groups.map(({ stage, deals: groupDeals }) => (
            <div key={stage}>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-bold uppercase tracking-widest text-[#8a8a8a]">{stage}</span>
                <div className="flex-1 h-px bg-[#3a3a3a]" />
                <span className="text-xs text-[#7a7a7a]">{groupDeals.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                {groupDeals.map((deal) => (
                  <OutreachCard key={deal.id} deal={deal} onClick={() => onSelectDeal(deal)} photoUrl={photoUrls[deal.id]}
                    onStageChange={onStageChange} onPromote={onPromote} readOnly={readOnly} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
